import { useQueries } from "@tanstack/react-query";
import { metricsApi } from "../api/metrics";
import { qk } from "../../lib/queryKeys";
import type { MetricsDistributionParams } from "../api/types";
import type { ComparisonGroup } from "./useComparisonGroups";

/**
 * Fetches a distribution for each comparison group in parallel using the
 * existing distribution endpoint (one request per group). Reuses the same query
 * keys as useMetricsDistribution so requests dedupe/cache with the rest of the app.
 */
export const useComparisonDistribution = (
  groups: ComparisonGroup[],
  metric: string
) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: MetricsDistributionParams = {
        ...group.filterParams,
        metric,
      };
      return {
        queryKey: qk.metrics.distribution(metric, params),
        queryFn: () => metricsApi.getDistribution(params),
        staleTime: 0,
        enabled:
          !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return groups.map((group, index) => ({
    group,
    data: results[index]?.data,
    isLoading: results[index]?.isLoading ?? false,
    isError: results[index]?.isError ?? false,
  }));
};
